import gsap from 'gsap';

// Offset rispetto alla fine del preloader (come React: video @1.6, titolo @2.2, nav @3.8).
var HERO_REVEAL = {
  video: 0,
  videoDuration: 1.2,
  lines: 0.6,
  linesDuration: 1.2,
  linesStagger: 0.1,
  subtitle: 1.8,
  subtitleDuration: 1,
  ctas: 2,
  ctasDuration: 1,
};

function dispatchCamstEvent(name, detail) {
  if (!window.CustomEvent) {
    return;
  }

  window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function pageHasPreloader() {
  return Boolean(document.querySelector('[data-camst-preloader]'));
}

function hasPreloaderCompleted() {
  var preloader = document.querySelector('[data-camst-preloader]');

  return Boolean(
    document.body.classList.contains('camst-preloader-complete') ||
      (preloader && preloader.dataset.camstPreloaderComplete === 'true')
  );
}

function hasPreloaderHandoff() {
  return document.body.classList.contains('camst-preloader-handoff');
}

function isHomePage() {
  return document.body.classList.contains('home') || document.body.classList.contains('front-page');
}

function getHeroTargets(root) {
  return {
    videoWrap: root.querySelector('[data-camst-hero-video-wrap]'),
    lines: root.querySelectorAll('.camst-hero__line-inner'),
    subtitle: root.querySelector('[data-camst-hero-subtitle]'),
    ctas: root.querySelector('[data-camst-hero-ctas]'),
  };
}

function setHeroInitialState(root) {
  var targets = getHeroTargets(root);

  if (targets.videoWrap) {
    gsap.set(targets.videoWrap, { scale: 0, opacity: 0, transformOrigin: 'center center' });
  }

  gsap.set(targets.lines, { yPercent: 100 });
  gsap.set([targets.subtitle, targets.ctas], { opacity: 0, y: 20 });
}

function setHeroVisible(root) {
  var targets = getHeroTargets(root);

  if (targets.videoWrap) {
    gsap.set(targets.videoWrap, { scale: 1, opacity: 1 });
  }

  gsap.set(targets.lines, { yPercent: 0 });
  gsap.set([targets.subtitle, targets.ctas], { opacity: 1, y: 0 });
  root.classList.add('is-revealed');
}

function notifyHeroVideoStarted(root) {
  // L'evento e' one-shot: quando il preloader ha gia' concluso al boot, questa
  // emissione e' sincrona e puo' precedere il listener del reveal header.
  // La classe lascia traccia dello stato, cosi' chi si registra dopo recupera.
  document.body.classList.add('camst-hero-video-started');

  dispatchCamstEvent('camst:hero:video-started', {
    hero: root,
  });
}

function playHeroVideo(root) {
  var video = root.querySelector('[data-camst-hero-video]');

  if (!video) {
    notifyHeroVideoStarted(root);
    return;
  }

  var notified = false;

  function emitVideoStarted() {
    if (notified) {
      return;
    }

    notified = true;
    notifyHeroVideoStarted(root);
  }

  video.addEventListener('playing', emitVideoStarted, { once: true });

  if (!video.paused && video.currentTime > 0) {
    emitVideoStarted();
    return;
  }

  var playPromise = video.play();

  if (playPromise && typeof playPromise.then === 'function') {
    playPromise.then(emitVideoStarted).catch(function () {
      window.setTimeout(emitVideoStarted, 400);
    });
    return;
  }

  window.setTimeout(emitVideoStarted, 2500);
}

/** Avvia buffer/playback del video hero all’handoff preloader. */
export function primeCamstHeroVideos() {
  document.querySelectorAll('[data-camst-hero]').forEach(function (root) {
    var video = root.querySelector('[data-camst-hero-video]');

    if (!video) {
      return;
    }

    video.muted = true;

    if (video.preload !== 'auto') {
      video.preload = 'auto';
    }

    if (video.readyState < 2) {
      try {
        video.load();
      } catch (error) {
        // ignore
      }
    }

    if (video.paused) {
      var playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          // Autoplay può essere bloccato dal browser fino al gesto utente.
        });
      }
    }
  });
}

export function runCamstHeroReveal(root) {
  if (!root || root.dataset.camstHeroRevealed === 'true') {
    return;
  }

  root.dataset.camstHeroRevealed = 'true';
  dispatchCamstEvent('camst:hero:reveal-started', { hero: root });
  playHeroVideo(root);

  if (prefersReducedMotion()) {
    setHeroVisible(root);
    notifyHeroVideoStarted(root);
    return;
  }

  var targets = getHeroTargets(root);

  var tl = gsap.timeline({
    onComplete: function () {
      root.classList.add('is-revealed');
    },
  });

  if (targets.videoWrap) {
    tl.to(
      targets.videoWrap,
      {
        scale: 1,
        opacity: 1,
        duration: HERO_REVEAL.videoDuration,
        ease: 'expo.out',
      },
      HERO_REVEAL.video
    );
  }

  if (targets.lines.length) {
    tl.to(
      targets.lines,
      {
        yPercent: 0,
        duration: HERO_REVEAL.linesDuration,
        stagger: HERO_REVEAL.linesStagger,
        ease: 'power4.out',
      },
      HERO_REVEAL.lines
    );
  }

  if (targets.subtitle) {
    tl.to(
      targets.subtitle,
      {
        opacity: 1,
        y: 0,
        duration: HERO_REVEAL.subtitleDuration,
        ease: 'power3.out',
      },
      HERO_REVEAL.subtitle
    );
  }

  if (targets.ctas) {
    tl.to(
      targets.ctas,
      {
        opacity: 1,
        y: 0,
        duration: HERO_REVEAL.ctasDuration,
        ease: 'power3.out',
      },
      HERO_REVEAL.ctas
    );
  }
}

function alignHeroToViewport(root) {
  root.style.marginTop = '';
  root.style.setProperty('--camst-hero-offset', '0px');

  var offset = Math.round(root.getBoundingClientRect().top);

  if (offset > 0) {
    root.style.marginTop = '-' + offset + 'px';
    root.style.setProperty('--camst-hero-offset', offset + 'px');
  }
}

function initHero(root) {
  if (!root || root.dataset.camstHeroReady === 'true') {
    return;
  }

  root.dataset.camstHeroReady = 'true';
  alignHeroToViewport(root);
  setHeroInitialState(root);

  window.addEventListener(
    'load',
    function () {
      alignHeroToViewport(root);
    },
    { once: true }
  );

  window.addEventListener('resize', function () {
    window.requestAnimationFrame(function () {
      alignHeroToViewport(root);
    });
  });

  var waitForPreloader = pageHasPreloader() && isHomePage();

  if (!waitForPreloader) {
    window.requestAnimationFrame(function () {
      runCamstHeroReveal(root);
    });
  }
}

export function initCamstHero(context) {
  var scope = context || document;
  var roots = scope.querySelectorAll('[data-camst-hero]');

  roots.forEach(function (root) {
    initHero(root);
  });
}

export function initCamstHeroPreloaderBridge() {
  var revealed = false;

  function revealHeroes() {
    if (revealed) {
      return;
    }

    revealed = true;
    document.querySelectorAll('[data-camst-hero]').forEach(function (root) {
      runCamstHeroReveal(root);
    });
  }

  if (hasPreloaderCompleted()) {
    revealHeroes();
    return;
  }

  if (hasPreloaderHandoff()) {
    revealHeroes();
    return;
  }

  window.addEventListener('camst:preloader:handoff', revealHeroes, { once: true });
  window.addEventListener('camst:preloader:closed', revealHeroes, { once: true });
}
